import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ChevronRight, Sparkles } from "lucide-react";

const SITUATIONS = [
  {
    id: "nu-vad-copilul",
    title: "Nu îmi văd copilul",
    intro: "Programul de vizită este blocat sistematic sau copilul este îndepărtat fizic.",
    do: ["Notează fiecare incident: data, ora, martori.", "Trimite cereri scrise de respectare a programului (cu confirmare).", "Solicită sprijin la Poliție/DGASPC pentru a documenta refuzul.", "Caută un avocat specializat pe dreptul familiei și alienare."],
    dont: ["Nu confrunta agresiv celălalt părinte față de copil.", "Nu folosi copilul ca mesager.", "Nu ceda emoțional — păstrează calmul absolut."],
  },
  {
    id: "copil-influentat",
    title: "Copil influențat emoțional",
    intro: "Copilul te respinge brusc, repetă lucruri pe care nu le poate ști, evită familia ta extinsă.",
    do: ["Validează emoțiile copilului — nu le contrazice.", "Menține contactul calm și consecvent.", "Documentează schimbările în comportament.", "Cere ghidare profesionistă pe alienare parentală."],
    dont: ["Nu vorbi de rău celălalt părinte.", "Nu te justifica emoțional în fața copilului.", "Nu insista cu argumente logice."],
  },
  {
    id: "acuzatii-false",
    title: "Acuzații false",
    intro: "Apar acuzații care nu reflectă realitatea — adesea ca strategie de îndepărtare.",
    do: ["Răspunde calm, scris, documentat.", "Solicită evaluare psihologică independentă.", "Angajează avocat specializat imediat.", "Păstrează toate dovezile (mesaje, audio dacă e legal, martori)."],
    dont: ["Nu reacționa agresiv online.", "Nu ataca persoana care a făcut acuzația — atacă faptele.", "Nu intra în contact direct fără martor sau înregistrare."],
  },
  {
    id: "dgaspc",
    title: "DGASPC nu respectă programul",
    intro: "Instituția nu acționează la sesizările tale sau ia decizii părtinitoare.",
    do: ["Sesizare scrisă cu număr de înregistrare.", "Escaladare la conducerea DGASPC județean.", "Sesizare la Avocatul Poporului.", "Sesizare la Ministerul Familiei dacă nu se acționează."],
    dont: ["Nu accepta răspunsuri verbale fără număr de înregistrare.", "Nu lăsa termene să expire — fă follow-up săptămânal."],
  },
  {
    id: "executor",
    title: "Executor blocat",
    intro: "Programul de vizită este stabilit prin instanță, dar executarea silită nu funcționează.",
    do: ["Cere executare cu sprijin de Poliție.", "Document fiecare refuz al celuilalt părinte.", "Sesizează încălcarea hotărârii la instanță (penal/civil)."],
    dont: ["Nu ceda — refuzul devine normă dacă nu îl contești."],
  },
  {
    id: "instanta",
    title: "Instanță și termene lungi",
    intro: "Procesul durează luni sau ani, iar copilul crește departe de tine.",
    do: ["Cere termene rapide invocând interesul superior al copilului.", "Solicită ordonanță președințială pentru program imediat.", "Caută probe documentate, nu emoționale.", "Lucrează cu un psiholog specializat care poate da expertiză."],
    dont: ["Nu pune presiune pe copil pentru declarații.", "Nu îți pierde calmul în instanță — totul se reține."],
  },
];

export default function Ajutor() {
  return (
    <div data-testid="page-ajutor" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-12 md:mb-16">
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Ghid practic</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#F8F8F4]">Ai nevoie de ajutor?</h1>
        <p className="mt-5 text-[#A0AABF] text-base md:text-lg max-w-2xl">Alege situația ta. Vei primi ghidare clară: ce faci, ce nu faci niciodată.</p>

        <Link to="/test" className="btn-gold rounded-sm px-6 py-3 inline-flex items-center gap-2 text-sm mt-6">
          <Sparkles className="w-4 h-4" /> Începe cu testul rapid (2 minute)
        </Link>
      </header>

      <div className="space-y-6">
        {SITUATIONS.map(s => (
          <section key={s.id} id={s.id} data-testid={`help-${s.id}`} className="cinematic-card rounded-lg p-6 md:p-8 scroll-mt-24">
            <h2 className="font-display text-2xl md:text-3xl text-[#F8F8F4] mb-2">{s.title}</h2>
            <p className="text-[#A0AABF] mb-6">{s.intro}</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm uppercase tracking-widest text-[#3BA76D] mb-3 flex items-center gap-2"><Check className="w-4 h-4" /> Ce faci</h3>
                <ul className="space-y-2">
                  {s.do.map((x, i) => <li key={i} className="text-[#F8F8F4]/85 text-sm md:text-base flex gap-2"><ChevronRight className="w-4 h-4 text-[#D4A63D] mt-1 shrink-0" /> {x}</li>)}
                </ul>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-widest text-[#C83A3A] mb-3 flex items-center gap-2"><X className="w-4 h-4" /> Ce NU faci</h3>
                <ul className="space-y-2">
                  {s.dont.map((x, i) => <li key={i} className="text-[#F8F8F4]/85 text-sm md:text-base flex gap-2"><ChevronRight className="w-4 h-4 text-[#C83A3A] mt-1 shrink-0" /> {x}</li>)}
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-3">
              <Link to="/programare" className="btn-gold rounded-sm px-5 py-2.5 text-sm">Vorbește cu Gabriel</Link>
              <Link to="/articole?category=sistem" className="btn-ghost-light rounded-sm px-5 py-2.5 text-sm">Articole relevante</Link>
              <Link to="/institutii" className="btn-ghost-light rounded-sm px-5 py-2.5 text-sm">Instituții utile</Link>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
